import { useEffect, useRef, useState } from 'react';
import type { ActiveSession, Exercise, SetResult, TemplateExercise } from '../shared/types';
import { formatExerciseResult } from '../shared/history';
import { calendarStatus, statusLabels, workoutProgress } from '../shared/progress';
import { api } from './api';
import { flushQueue, saveSessionRecord } from './offline';
import { ErrorNotice, Field, Modal, Spinner, formatDate, formatDuration } from './components';
import { loadWorkoutRecord, resumeWorkoutRecord } from './session-access';

export async function syncBeforeEdit(profileId: string) {
  if (!navigator.onLine) throw new Error('Connect to edit saved records. Your workout progress stays saved on this phone.');
  const result = await flushQueue(profileId);
  if (result.remaining) throw new Error('Your latest changes are still syncing. Try again after they finish.');
}

export function LoggedSets({ sets, item, onEdit }: { sets: SetResult[]; item: TemplateExercise; onEdit: (set: SetResult) => void }) {
  const rows = sets.filter(set => set.templateExerciseId === item.id).sort((a,b) => a.setNumber-b.setNumber);
  return <section className="logged-sets" aria-label={`Completed sets for ${item.exercise.name}`}>
    <h3>Completed sets</h3>
    {rows.length ? <ol>{rows.map(set => <li key={set.clientEventId}>
      <div><strong>Set {set.setNumber}</strong><span>{formatExerciseResult(set.loadMode ?? item.exercise.loadMode, set)}</span>
        {set.exerciseId !== item.exercise.id && <small>{set.name ?? 'Different exercise'}</small>}</div>
      <button type="button" onClick={() => onEdit(set)} aria-label={`Edit set ${set.setNumber} of ${item.exercise.name}`}>Edit</button>
    </li>)}</ol> : <p className="muted small">No sets logged yet.</p>}
  </section>;
}

export function SetEditor({ sessionId, profileId, set, onSaved, onClose }: {
  sessionId: string; profileId: string; set: SetResult; onSaved: (record: ActiveSession) => void; onClose: () => void;
}) {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [exerciseId, setExerciseId] = useState(set.exerciseId);
  const [weight, setWeight] = useState(String(set.weightValue ?? ''));
  const [reps, setReps] = useState(String(set.reps ?? ''));
  const [count, setCount] = useState(String(set.countValue ?? ''));
  const [number, setNumber] = useState(String(set.setNumber));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const eventId = useRef(crypto.randomUUID());
  const mode = exercises.find(e => e.id === exerciseId)?.loadMode;
  useEffect(() => { api<Exercise[]>('/exercises').then(setExercises).catch(e => setError(e.message)); }, []);
  async function save() {
    if (!mode || busy) return;
    setBusy(true); setError('');
    try {
      await syncBeforeEdit(profileId);
      const record = await api<ActiveSession>(`/records/${sessionId}/sets/${set.clientEventId}`, { method: 'PATCH',
        headers: { 'X-Hardnup-Profile': profileId }, body: JSON.stringify({ clientEventId: eventId.current, exerciseId, setNumber: Number(number),
          weightValue: mode === 'count' ? null : mode === 'bodyweight' && !weight.trim() ? 0 : weight.trim() ? Number(weight) : null,
          reps: mode === 'count' ? null : reps.trim() ? Number(reps) : null,
          countValue: mode === 'count' && count.trim() ? Number(count) : null, expectedEditedAt: set.editedAt ?? null }) });
      await saveSessionRecord(profileId, record);
      onSaved(record);
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not save this set.'); }
    finally { setBusy(false); }
  }
  return <Modal title={`Edit logged set ${set.setNumber}`} onClose={() => { if (!busy) onClose(); }}>
    {error && <ErrorNotice message={error} />}
    <form onSubmit={e => { e.preventDefault(); void save(); }}>
      <Field label="Exercise"><select aria-label="Exercise" value={exerciseId} onChange={e => { setExerciseId(e.target.value); setWeight(''); setReps(''); setCount(''); }} disabled={busy}>
        {exercises.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
      </select></Field>
      <Field label="Set number"><input required type="number" min="1" step="1" value={number} onChange={e => setNumber(e.target.value)} disabled={busy}/></Field>
      {mode !== 'count' && <Field label={mode === 'bodyweight' ? 'Added weight kg (optional)' : mode === 'kg_each' ? 'Weight kg each' : 'Weight kg'}>
        <input type="number" inputMode="decimal" min="0" step="any" required={mode !== 'bodyweight'} value={weight} onChange={e => setWeight(e.target.value)} disabled={busy}/>
      </Field>}
      {mode === 'count' ? <Field label="Count"><input required type="number" inputMode="numeric" min="1" step="1" value={count} onChange={e => setCount(e.target.value)} disabled={busy}/></Field>
        : <Field label="Reps"><input required type="number" inputMode="numeric" min="1" step="1" value={reps} onChange={e => setReps(e.target.value)} disabled={busy}/></Field>}
      <button className="button button--primary button--full" disabled={busy || !mode}>{busy ? 'Saving…' : 'Save changes'}</button>
    </form>
  </Modal>;
}

export function ExerciseEditor({ sessionId, profileId, item, onSaved, onClose }: {
  sessionId: string; profileId: string; item: TemplateExercise; onSaved: (record: ActiveSession) => void; onClose: () => void;
}) {
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [id, setId] = useState(item.exercise.id);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  const eventId = useRef(crypto.randomUUID());
  useEffect(() => { api<Exercise[]>('/exercises').then(setExercises).catch(e => setError(e.message)); }, []);
  return <Modal title="Edit exercise" onClose={() => { if (!busy) onClose(); }}>
    {error && <ErrorNotice message={error}/>}
    <Field label="Exercise"><select aria-label="Exercise" value={id} onChange={e => setId(e.target.value)}>{exercises.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}</select></Field>
    <p className="muted">This changes the exercise for this workout. Logged sets keep their recorded exercise; use Edit on a set to correct it.</p>
    <button className="button button--primary button--full" disabled={busy || !exercises.length} onClick={() => void (async () => {
      setBusy(true); setError('');
      try { await syncBeforeEdit(profileId); const record = await api<ActiveSession>(`/records/${sessionId}/exercises/${item.id}`, { method: 'PATCH', headers: { 'X-Hardnup-Profile': profileId }, body: JSON.stringify({ clientEventId: eventId.current, exerciseId: id }) });
        await saveSessionRecord(profileId, record); onSaved(record);
      } catch(e) { setError(e instanceof Error ? e.message : 'Could not save exercise.'); } finally { setBusy(false); }
    })()}>Save exercise</button>
  </Modal>;
}

export function SessionRecord({ id, profileId, onClose, onResume, onChanged }: {
  id: string; profileId: string; onClose: () => void; onResume: (session: ActiveSession) => void; onChanged: () => void;
}) {
  const [record, setRecord] = useState<ActiveSession | null>(null);
  const [edit, setEdit] = useState<SetResult | null>(null);
  const [editExercise, setEditExercise] = useState<TemplateExercise | null>(null);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(false);
  useEffect(() => { let current = true; setRecord(null); loadWorkoutRecord(profileId,id).then(value => {if(current) setRecord(value);}).catch(e => {if(current) setError(e.message);}); return ()=>{current=false;}; }, [id,profileId]);
  const saved = (updated: ActiveSession) => { setRecord(updated); setEdit(null); setEditExercise(null); onChanged(); };
  async function action(kind: 'resume' | 'close') {
    if (busy || !record) return;
    if (kind === 'close' && !window.confirm('Close this workout as incomplete? Logged sets stay editable, but this workout can no longer be resumed.')) return;
    setBusy(true); setError('');
    try { if(kind==='resume') { onResume(await resumeWorkoutRecord(profileId,id)); return; }
      await syncBeforeEdit(profileId); const updated = await api<ActiveSession>(`/records/${id}/${kind}`, { method:'POST', headers:{'X-Hardnup-Profile':profileId}, body:JSON.stringify({clientEventId:crypto.randomUUID(),confirmed:true}) });
      await saveSessionRecord(profileId,updated); saved(updated);
    } catch(e) { setError(e instanceof Error ? e.message : 'Could not update workout.'); } finally { setBusy(false); }
  }
  if(edit) return <SetEditor sessionId={id} profileId={profileId} set={edit} onSaved={saved} onClose={() => setEdit(null)}/>;
  if(editExercise) return <ExerciseEditor sessionId={id} profileId={profileId} item={editExercise} onSaved={saved} onClose={() => setEditExercise(null)}/>;
  const progress = record && workoutProgress(record.template,record.sets);
  return <Modal title={record?.template.name ?? 'Workout details'} onClose={onClose}>
    {error && <ErrorNotice message={error}/>}
    {!record ? <Spinner/> : <>
      <p><strong>{statusLabels[calendarStatus(record.status ?? 'active',record.sets.length)]} · {progress!.percentage}%</strong> · {progress!.completed}/{progress!.prescribed} sets</p>
      <p>{formatDate(record.startedAt)} · {formatDuration(record.elapsedSeconds)}</p>
      {['active','partial'].includes(record.status ?? '') && <div className="record-actions">
        <button className="button button--primary" disabled={busy} onClick={() => void action('resume')}>Resume workout</button>
        <button className="button button--danger-ghost" disabled={busy} onClick={() => void action('close')}>Close as incomplete</button>
      </div>}
      {record.template.exercises.map(item => <section key={item.id} className="record-exercise">
        <h3>{item.exercise.name}</h3>
        {item.finishedEarly && <p className="muted small">Finished early · {record.sets.filter(s => s.templateExerciseId === item.id).length}/{item.sets} sets</p>}
        <button className="text-button" onClick={() => setEditExercise(item)}>Edit exercise</button>
        <LoggedSets item={item} sets={record.sets} onEdit={setEdit}/>
      </section>)}
      {record.notes && <p>{record.notes}</p>}
      {record.exerciseEvents?.length ? <section><h3>Workout changes &amp; exercise notes</h3><ul>{record.exerciseEvents.map((event,index)=><li key={index}>{event.exerciseName}: {event.kind}{event.detail ? ` · ${event.detail}` : ''}</li>)}</ul></section> : null}
    </>}
  </Modal>;
}
